import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { usePwaUpdate, UPDATE_PHASE_LABEL } from '../hooks/usePwaUpdate';

/**
 * Obrazovka pre prípad, keď appka siahne po súbore z generácie, ktorú server
 * už neservíruje.
 *
 * Zámerne tu nie je "Skúsiť znova" ani obnovenie stránky: oboje vráti tú istú
 * generáciu, lebo index dodáva service worker zo svojej cache — a presne preto
 * vyzeralo predtým klepnutie na záložku ako trvalo rozbitá appka. Von vedie
 * jediná cesta: vymeniť generáciu. To isté tlačidlo je v Nastaveniach, ale
 * práve tam sa človek v tomto stave nemusí dostať, lebo aj Nastavenia sú
 * samostatný súbor. Preto je tu.
 *
 * Musí ostať v hlavnom balíku. Obrazovka, ktorá sa načítava, by tento problém
 * mala rovnako a nemal by ju kto ukázať.
 */
export const StaleVersionScreen = () => {
  const { phase, progress, error, applyUpdate } = usePwaUpdate();
  const busy = ['checking', 'downloading', 'installing', 'restarting'].includes(phase);

  return (
    <div className="flex min-h-screen items-center justify-center p-5">
      <div className="panel w-full max-w-md p-6 text-center">
        <h1 className="t-title">Je vonku novšia verzia</h1>
        <p className="mt-2 text-[15px] text-slate-500 dark:text-slate-400">
          Táto obrazovka patrí k novšiemu vydaniu, než aké máš spustené. Tvoje dáta sú v poriadku —
          stačí nainštalovať aktualizáciu a appka sa reštartuje.
        </p>

        {busy || phase === 'error' ? (
          <div className="mt-4" aria-live="polite">
            <div className="flex items-center justify-between gap-3 text-[13px]">
              <span>{UPDATE_PHASE_LABEL[phase]}</span>
              {busy ? <strong className="tabular-nums">{Math.round(progress)} %</strong> : null}
            </div>
            {busy ? (
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-black/[0.06] dark:bg-white/10">
                <span
                  className="block h-full rounded-full bg-brand-500 transition-[width] duration-100"
                  style={{ width: `${progress}%` }}
                />
              </div>
            ) : null}
            {error ? (
              <p className="mt-2 text-[12px] text-red-600 dark:text-red-400">{error}</p>
            ) : null}
          </div>
        ) : null}

        <div className="mt-5 flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            className="btn-primary flex-1"
            onClick={() => void applyUpdate()}
            disabled={busy}
          >
            <ArrowDownTrayIcon className="mr-2 h-5 w-5" />
            {busy ? UPDATE_PHASE_LABEL[phase] : 'Nainštalovať novú verziu'}
          </button>
          {/* Prehľad je v hlavnom balíku, takže sa načíta aj v tomto stave. */}
          <button
            type="button"
            className="btn-secondary flex-1"
            onClick={() => {
              window.location.href = '/';
            }}
          >
            Na úvod
          </button>
        </div>
      </div>
    </div>
  );
};
